import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Linkedin,
  Globe,
  PlayCircle,
  MapPin,
} from "lucide-react";

const certs = [
  "SPC · SAFe 5",
  "CSAT Trainer",
  "CTC Coach",
  "Educator CAL",
  "CSPO · CSP-SM",
  "A-CSM · CSM",
];

const deliverables = [
  "Digital IT transformation strategy & implementation for large enterprises",
  "Customer-centric product strategy aligned to business goals",
  "Coaching executive leadership & management teams",
  "Building Digital Transformation Programmes & Product Innovation",
  "Training & enabling agile organisations at scale (SAFe, Scrum)",
];

const sweden = [
  "Active member of Agile Sweden / Agila Sverige & Agila People Sweden",
  "Prior client work delivered in Sweden",
  "Familiarity with Swedish business culture and collaborative leadership style",
  "Member of Scrum Alliance review committee — global network, local insight",
  "Speaker at TechArena 2026 (Stockholm, February)",
];

const clients = [
  "IBM",
  "Ericsson",
  "Olympus",
  "Orange Group",
  "Swedbank",
  "Twitter",
  "OTIS",
  "Visa Inc",
  "Crisp AB",
];

const memberships = [
  "Scrum Alliance Member",
  "ICAgile Member Organisation",
  "Management 3.0 Practitioner",
  "LeanKanban University",
  "Agile Alliance",
  "Agila Sverige",
  "Agila People Sweden",
];

const metrics = [
  { num: "Fortune 500", label: "Global companies transformed across sectors" },
  { num: "Sweden", label: "Proven client delivery including Swedish organisations" },
  { num: "20+ years", label: "Leading digital product & agile transformation" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Section = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={fadeUp}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const SwedishFlag = () => (
  <span
    aria-hidden
    className="inline-block relative rounded-[2px] overflow-hidden shrink-0"
    style={{ width: 22, height: 15, background: "#006AA7" }}
  >
    <span
      className="absolute top-0 bottom-0"
      style={{ left: 6, width: 3, background: "#FECC02" }}
    />
    <span
      className="absolute left-0 right-0"
      style={{ top: "50%", height: 3, background: "#FECC02", transform: "translateY(-50%)" }}
    />
  </span>
);

export default function LandingPage() {
  useEffect(() => {
    document.title = "Elena Vassilieva — Agile Coach & Digital Transformation Leader";
  }, []);

  return (
    <div
      data-testid="elena-landing-page"
      className="min-h-screen bg-[#1a0606] text-[#fbe9df] overflow-x-hidden selection:bg-[#ff5c36] selection:text-[#1a0606]"
      style={{ fontFamily: "'Outfit', 'Inter', system-ui, sans-serif" }}
    >
      {/* Grain */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      {/* Ambient glows */}
      <div
        aria-hidden
        className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-30"
        style={{ background: "radial-gradient(circle, #ff3d7f 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none fixed -bottom-40 -right-40 w-[620px] h-[620px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(circle, #ff7a4b 0%, transparent 70%)" }}
      />

      {/* ── HERO ────────────────────────────────────────── */}
      <Section
        id="top"
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-24 pb-14 md:pb-24"
      >
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          {/* Left — name & details */}
          <div className="md:col-span-8">
            <motion.div
              variants={fadeUp}
              custom={0}
              className="inline-flex items-center gap-3 text-sm md:text-base tracking-[0.18em] uppercase text-[#ff8866] mb-8 font-semibold"
              data-testid="hero-availability"
            >
              <SwedishFlag />
              Available for engagements in Sweden &amp; internationally
            </motion.div>

            <motion.h1
              variants={fadeUp}
              custom={1}
              data-testid="hero-name"
              className="font-light leading-[0.92] tracking-tight"
              style={{
                fontSize: "clamp(3.2rem, 9vw, 8rem)",
                color: "#fbe9df",
                letterSpacing: "-0.035em",
              }}
            >
              <span style={{ color: "#ff5c36" }}>Elena</span>
              <br />
              <span
                style={{
                  background:
                    "linear-gradient(95deg, #ff3d7f 0%, #ff7a4b 55%, #ffa84b 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Vassilieva.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              custom={2}
              data-testid="hero-tagline"
              className="mt-8 text-lg md:text-2xl font-light text-[#fbe9df]/85 max-w-2xl leading-snug"
            >
              Agile Coach · Digital Transformation Leader · Executive Trainer
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={3}
              data-testid="hero-trust"
              className="mt-10 flex items-center gap-3 text-base md:text-lg text-[#fbe9df]/90"
            >
              <span
                className="inline-block h-px w-10"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, #ff5c36 100%)",
                }}
              />
              Trusted by{" "}
              <span className="font-semibold text-[#ff8866]">Fortune 500</span>{" "}
              leaders for{" "}
              <span className="font-semibold text-[#ff8866]">20+ years</span>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={4}
              data-testid="hero-certs"
              className="mt-6 flex flex-wrap gap-3"
            >
              {certs.map((c) => (
                <span
                  key={c}
                  className="text-base md:text-lg font-bold px-6 py-3 rounded-full border-[1.5px] border-[#ff5c36]/70 text-[#fbe9df] bg-[#ff5c36]/[0.14] backdrop-blur-sm shadow-[0_0_0_1px_rgba(255,92,54,0.08),0_4px_18px_-8px_rgba(255,92,54,0.45)]"
                >
                  {c}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Right — Portrait + Contact panel */}
          <motion.div
            variants={fadeUp}
            custom={3}
            className="md:col-span-4"
            data-testid="hero-contact"
          >
            <div
              className="rounded-3xl p-[1.5px]"
              style={{
                background:
                  "linear-gradient(160deg, rgba(255,61,127,0.6) 0%, rgba(255,122,75,0.6) 50%, rgba(255,168,75,0.6) 100%)",
              }}
            >
              <div className="rounded-[22px] bg-[#1a0606]/95 backdrop-blur p-5 md:p-6">
                {/* Portrait */}
                <div
                  className="aspect-[4/5] rounded-2xl relative overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(155deg, #ff5c8f 0%, #ff7a4b 60%, #ffb066 100%)",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_page-craft-393/artifacts/wja3frr6_Elena_headshot_2.jpg"
                    alt="Elena Vassilieva"
                    data-testid="speaker-portrait"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ mixBlendMode: "luminosity", opacity: 0.92 }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 60%, rgba(26,6,6,0.55) 100%)",
                    }}
                  />
                </div>

                <div className="mt-6">
                  <div className="text-xs tracking-[0.28em] uppercase text-[#ff5c36] font-bold">
                    Get in touch
                  </div>
                  <div className="mt-5 space-y-4">
                    <a
                      data-testid="contact-linkedin"
                      href="https://www.linkedin.com/in/elenavassilieva/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 text-[#fbe9df]/90 hover:text-[#ff5c36] transition-colors"
                    >
                      <Linkedin className="w-5 h-5 text-[#ff5c36] shrink-0" />
                      <span className="text-base">linkedin.com/in/elenavassilieva</span>
                      <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                    <a
                      data-testid="contact-website"
                      href="https://www.evassilieva.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 text-[#fbe9df]/90 hover:text-[#ff5c36] transition-colors"
                    >
                      <Globe className="w-5 h-5 text-[#ff5c36] shrink-0" />
                      <span className="text-base">evassilieva.com</span>
                      <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                    <a
                      data-testid="contact-video"
                      href="https://youtu.be/we10ekNwNz0"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 text-[#fbe9df]/90 hover:text-[#ff5c36] transition-colors"
                    >
                      <PlayCircle className="w-5 h-5 text-[#ff5c36] shrink-0" />
                      <span className="text-base">Video introduction</span>
                      <ArrowUpRight className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>

                  <div className="mt-5 flex items-center gap-2 text-sm text-[#fbe9df]/65">
                    <MapPin className="w-4 h-4 text-[#ff5c36] shrink-0" />
                    Available in Stockholm &amp; remote
                  </div>

                  <a
                    data-testid="hero-cta-linkedin"
                    href="https://www.linkedin.com/in/elenavassilieva/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 group inline-flex w-full items-center justify-center gap-2 px-5 py-4 rounded-full font-bold text-[#1a0606] text-base shadow-[0_8px_30px_-8px_rgba(255,92,54,0.6)] transition-transform hover:-translate-y-0.5"
                    style={{
                      background:
                        "linear-gradient(95deg, #ff3d7f 0%, #ff7a4b 55%, #ffa84b 100%)",
                    }}
                  >
                    Connect on LinkedIn
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* ── METRICS STRIP ───────────────────────────────── */}
      <Section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div
          className="grid md:grid-cols-3 rounded-3xl overflow-hidden border border-[#ff5c36]/20"
          data-testid="metrics-strip"
          style={{
            background:
              "linear-gradient(95deg, rgba(255,61,127,0.10) 0%, rgba(255,122,75,0.10) 100%)",
          }}
        >
          {metrics.map((m, i) => (
            <div
              key={m.num}
              className={`px-7 py-8 md:py-10 ${
                i < metrics.length - 1 ? "md:border-r border-[#ff5c36]/15" : ""
              }`}
              data-testid={`metric-${i}`}
            >
              <div
                className="text-2xl md:text-4xl font-light tracking-tight"
                style={{ color: "#ff5c36" }}
              >
                {m.num}
              </div>
              <div className="mt-2 text-sm text-[#fbe9df]/70 max-w-xs">
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ── WHAT I DELIVER + WHY SWEDEN ─────────────────── */}
      <Section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-12">
        <div className="grid md:grid-cols-2 gap-6" data-testid="two-col-section">
          {/* Card: What I deliver */}
          <div
            id="deliver"
            className="relative rounded-3xl border border-[#ff5c36]/15 p-7 md:p-9 bg-gradient-to-br from-[#220909] to-[#1a0606] overflow-hidden"
            data-testid="card-deliver"
            style={{ borderLeft: "3px solid #ff5c36" }}
          >
            <div
              aria-hidden
              className="absolute -right-16 -top-16 w-56 h-56 rounded-full blur-3xl opacity-25"
              style={{ background: "radial-gradient(circle,#ff3d7f,transparent 70%)" }}
            />
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#ff5c36] font-bold">
              What I deliver
            </div>
            <ul className="mt-6 divide-y divide-[#ff5c36]/10">
              {deliverables.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-3 py-3 text-[15px] text-[#fbe9df]/90 leading-relaxed"
                >
                  <span
                    className="mt-2 block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: "#ff5c36" }}
                  />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Card: Why Sweden */}
          <div
            id="sweden"
            className="relative rounded-3xl border border-[#ff5c36]/15 p-7 md:p-9 bg-gradient-to-br from-[#220909] to-[#1a0606] overflow-hidden"
            data-testid="card-sweden"
            style={{ borderLeft: "3px solid #ff7a4b" }}
          >
            <div
              aria-hidden
              className="absolute -left-16 -bottom-16 w-56 h-56 rounded-full blur-3xl opacity-25"
              style={{ background: "radial-gradient(circle,#ff7a4b,transparent 70%)" }}
            />
            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#ff5c36] font-bold">
                Why Sweden
              </div>
              <SwedishFlag />
            </div>
            <ul className="mt-6 divide-y divide-[#ff5c36]/10">
              {sweden.map((d) => (
                <li
                  key={d}
                  className="flex items-start gap-3 py-3 text-[15px] text-[#fbe9df]/90 leading-relaxed"
                >
                  <span
                    className="mt-2 block w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: "#ff7a4b" }}
                  />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* ── CLIENTS ─────────────────────────────────────── */}
      <Section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-6">
        <div
          id="clients"
          className="rounded-3xl border border-[#ff5c36]/15 p-8 md:p-12 bg-[#1a0606] relative overflow-hidden"
          data-testid="clients-section"
        >
          <div
            aria-hidden
            className="absolute -right-20 -top-20 w-72 h-72 rounded-full blur-3xl opacity-20"
            style={{ background: "radial-gradient(circle,#ff3d7f,transparent 70%)" }}
          />
          <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
              <div className="text-xs md:text-sm tracking-[0.28em] uppercase text-[#ff5c36] font-bold">
                Selected clients &amp; partners
              </div>
              <h3
                className="mt-4 font-light text-[#fbe9df] leading-[1.05]"
                style={{
                  fontSize: "clamp(1.75rem, 3vw, 2.75rem)",
                  letterSpacing: "-0.02em",
                }}
              >
                Trusted by global leaders.
              </h3>
            </div>
            <p className="text-base md:text-lg text-[#fbe9df]/60 max-w-sm">
              Engagements across telecom, finance, healthcare, manufacturing
              and tech.
            </p>
          </div>
          <div
            className="relative flex flex-wrap gap-3 md:gap-4"
            data-testid="clients-pills"
          >
            {clients.map((c) => (
              <span
                key={c}
                className="text-base md:text-lg font-bold px-6 py-3 rounded-full border-[1.5px] border-[#ff5c36]/70 text-[#fbe9df] bg-[#ff5c36]/[0.14] backdrop-blur-sm shadow-[0_0_0_1px_rgba(255,92,54,0.08),0_4px_18px_-8px_rgba(255,92,54,0.45)] hover:border-[#ff5c36] hover:bg-[#ff5c36]/[0.2] hover:text-[#ffd9c2] transition-all cursor-default"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* ── COMMUNITY ───────────────────────────────────── */}
      <Section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div
          id="community"
          className="rounded-3xl border border-[#ff5c36]/15 p-7 md:p-9 bg-[#1a0606]"
          data-testid="community-section"
        >
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#ff5c36] font-bold">
            Community &amp; memberships
          </div>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="memberships-list">
            {memberships.map((m) => (
              <span
                key={m}
                className="text-base md:text-lg font-semibold px-6 py-3 rounded-full border border-[#ff5c36]/40 text-[#fbe9df] bg-[#ff5c36]/[0.08] backdrop-blur-sm"
              >
                {m}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* ── FINAL CTA ───────────────────────────────────── */}
      <Section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20">
        <div
          className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #ff3d7f 0%, #ff7a4b 60%, #ffa84b 100%)",
          }}
          data-testid="final-cta"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
            }}
          />
          <div className="relative">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#1a0606]/70 font-bold mb-5">
              Let&apos;s work together
            </div>
            <h3
              className="font-light text-[#1a0606] leading-[0.95]"
              style={{
                fontSize: "clamp(2.2rem, 5.5vw, 4.5rem)",
                letterSpacing: "-0.025em",
              }}
            >
              Ready to transform
              <br /> your organisation?
            </h3>
            <p className="mt-7 text-[#1a0606]/80 max-w-xl mx-auto text-base md:text-lg">
              Let&apos;s discuss how agile leadership and AI-empowered teams can
              accelerate your next chapter — coaching, training and executive
              advisory, in Sweden &amp; internationally.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <a
                data-testid="final-cta-linkedin"
                href="https://www.linkedin.com/in/elenavassilieva/"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#1a0606] text-[#fbe9df] font-medium hover:bg-[#2a0a0a] transition-colors"
              >
                <Linkedin className="w-5 h-5" />
                Message on LinkedIn
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                data-testid="final-cta-site"
                href="https://www.evassilieva.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-[#1a0606] text-[#1a0606] font-medium hover:bg-[#1a0606] hover:text-[#fbe9df] transition-colors"
              >
                Visit evassilieva.com
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ──────────────────────────────────────── */}
      <footer
        data-testid="footer"
        className="relative z-10 bg-[#120303] border-t border-[#ff5c36]/15"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <p className="text-xs text-[#fbe9df]/50">
            Open to coaching, training, consulting &amp; speaking engagements · Sweden &amp; international
          </p>
          <div className="flex gap-6 text-xs text-[#fbe9df]/55">
            <a className="hover:text-[#ff5c36] transition-colors inline-flex items-center gap-1.5" target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/in/elenavassilieva/">
              <Linkedin className="w-3 h-3" /> LinkedIn
            </a>
            <a className="hover:text-[#ff5c36] transition-colors inline-flex items-center gap-1.5" target="_blank" rel="noopener noreferrer" href="https://www.evassilieva.com">
              <Globe className="w-3 h-3" /> Website
            </a>
            <a className="hover:text-[#ff5c36] transition-colors inline-flex items-center gap-1.5" target="_blank" rel="noopener noreferrer" href="https://youtu.be/we10ekNwNz0">
              <PlayCircle className="w-3 h-3" /> Video intro
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
